import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  MonitorSmartphone,
  AppWindow,
  BarChart,
  Menu,
  Layers,
  Tags,
  Users,
  ClipboardList,
  Settings,
  WrenchIcon,
  ShieldAlert,
  Shield,
  SlidersHorizontal,
  FileSearch,
  Building2,
  FileText,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { EnableNotificationsButton } from "@/components/pwa/EnableNotificationsButton";

type NavEntry = { title: string; url: string; icon: LucideIcon };

/**
 * Primary tabs always visible at the bottom on phone viewports.
 * Kept to 4 so each tab has comfortable touch room next to the More button.
 */
const PRIMARY_TABS: NavEntry[] = [
  { title: "Dashboard", url: "/analytics", icon: BarChart },
  { title: "Devices", url: "/devices", icon: MonitorSmartphone },
  { title: "Apps", url: "/applications", icon: AppWindow },
];

/**
 * Everything else lives behind the More sheet. Order roughly mirrors the
 * desktop sidebar so muscle memory carries over.
 */
const MORE_ENTRIES: NavEntry[] = [
  { title: "Getting Started", url: "/getting-started", icon: FileText },
  { title: "Configurations", url: "/configurations", icon: Layers },
  { title: "Privacy Permissions", url: "/configurations/pppc", icon: Shield },
  { title: "App Configuration", url: "/configurations/app-config", icon: SlidersHorizontal },
  { title: "Patching", url: "/patching", icon: WrenchIcon },
  { title: "Vulnerabilities", url: "/vulnerabilities", icon: ShieldAlert },
  { title: "Tags", url: "/tags", icon: Tags },
  { title: "Users & Roles", url: "/admin/users-and-roles", icon: Users },
  { title: "Enrollment Profiles", url: "/admin/enrollment-profiles", icon: ClipboardList },
  { title: "Tenants", url: "/admin/tenants", icon: Building2 },
  { title: "Logs", url: "/logs", icon: FileSearch },
  { title: "Settings", url: "/settings", icon: Settings },
];

const isActive = (pathname: string, url: string) =>
  pathname === url || pathname.startsWith(url + "/");

/**
 * Mobile-only bottom tab bar + "More" sheet for everything that doesn't fit.
 * Hidden on `md+` viewports where the desktop sidebar takes over.
 */
export function MobileNav() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur-md md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="grid grid-cols-4">
        {PRIMARY_TABS.map((tab) => {
          const active = isActive(location.pathname, tab.url);
          const Icon = tab.icon;
          return (
            <li key={tab.url} className="relative">
              <NavLink
                to={tab.url}
                onClick={() => {
                  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
                    try { navigator.vibrate(8); } catch { /* ignore */ }
                  }
                }}
                className={cn(
                  "tap-feedback relative flex h-14 min-h-[44px] flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
                aria-current={active ? "page" : undefined}
              >
                <span
                  aria-hidden
                  className={cn(
                    "absolute top-0 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-primary transition-opacity",
                    active ? "opacity-100" : "opacity-0"
                  )}
                />
                <Icon
                  className={cn(
                    "h-5 w-5 transition-transform duration-200",
                    active && "scale-110"
                  )}
                  aria-hidden
                />
                <span>{tab.title}</span>
              </NavLink>
            </li>
          );
        })}
        <li>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="tap-feedback flex h-14 min-h-[44px] w-full flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Open more navigation"
                onClick={() => {
                  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
                    try { navigator.vibrate(8); } catch { /* ignore */ }
                  }
                }}
              >
                <Menu className="h-5 w-5" aria-hidden />
                <span>More</span>
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="max-h-[85svh] overflow-y-auto rounded-t-2xl"
            >
              <SheetHeader className="text-left">
                <SheetTitle>More</SheetTitle>
              </SheetHeader>
              <div className="mt-4">
                <EnableNotificationsButton />
              </div>
              <ul className="mt-4 divide-y divide-border">
                {MORE_ENTRIES.map((entry) => {
                  const active = isActive(location.pathname, entry.url);
                  const Icon = entry.icon;
                  return (
                    <li key={entry.url}>
                      <NavLink
                        to={entry.url}
                        onClick={() => setOpen(false)}
                        className={cn(
                          "tap-feedback flex min-h-[44px] items-center gap-3 py-3 text-[15px] transition-colors",
                          active ? "text-primary font-medium" : "text-foreground hover:text-primary"
                        )}
                      >
                        <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
                        <span>{entry.title}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </SheetContent>
          </Sheet>
        </li>
      </ul>
    </nav>
  );
}

export default MobileNav;
